import React, { useEffect, useState } from 'react';

import { PanelsEnum } from '@ariksa/inventory-core/api';
import { Box, Center, HStack, useDisclosure } from '@chakra-ui/react';
import { isEmpty, map, sumBy } from 'lodash';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  AriksaIcon,
  formatNumberWithComma,
  getIcon,
  getIconColor,
  WithResourceIcon,
} from 'components/DataDisplay';
import { DashboardTable } from 'components/DataDisplay/NewTable/DashboardTable';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { IconTypes } from 'components/Icons';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { selectCloudPostureOverview } from 'containers/Dashboard/CloudPostureOverview/selectors';
import { AllEntitiesDrawer } from 'containers/Dashboard/Drawers/AllEntitiesDrawer';
import { AllEntitiesDetailsProps } from 'containers/Dashboard/types';
import { renderNoEntitiesFoundWithIcon } from 'containers/Dashboard/utils';

export const WellKnownPorts: React.FC = () => {
  const { vmPortUsage } = useSelector(selectCloudPostureOverview);
  const allEntitiesDisclosure = useDisclosure();
  const [details, setDetails] = useState<AllEntitiesDetailsProps>(
    {} as AllEntitiesDetailsProps,
  );
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setTotal(sumBy(vmPortUsage.data, o => o.count));
  }, [vmPortUsage.data]);

  const columns = [
    {
      header: <Box pl={10}>PORT</Box>,
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={IconTypes.Port}
          color={getIconColor(IconTypes.Aws)}
        >
          {row.port}
        </WithResourceIcon>
      ),
      styles: { w: '30%' },
    },
    {
      header: 'ENTITIES',
      accessor: 'count',
      render: ({ value }) => formatNumber(value, 1),
      styles: {
        w: '20%',
        cell: { textAlign: 'right', w: '70%' },
        header: { textAlign: 'center' },
      },
    },
    {
      header: 'RISKS',
      render: ({ row }) => (
        <HStack spacing={2}>
          {map(row.contexts, c => (
            <CustomTooltip label={c}>
              <Box boxSize={5} color="critical">
                <Center>{getIcon(c)}</Center>
              </Box>
            </CustomTooltip>
          ))}
        </HStack>
      ),
      styles: { w: '50%' },
    },
  ];

  return (
    <>
      <DashboardWidget
        label="Well-known Ports Permitting 0.0.0.0/0"
        isLoading={vmPortUsage.isLoading}
        noData={isEmpty(vmPortUsage.data)}
        noDataMessage={renderNoEntitiesFoundWithIcon()}
        subHeader={
          (vmPortUsage.data?.length ?? 0) +
          ' port' +
          (vmPortUsage.data?.length === 1 ? ', ' : 's, ') +
          formatNumberWithComma(total) +
          ' entit' +
          (total === 1 ? 'y' : 'ies')
        }
        styles={{ contentWrapper: { px: 0, pb: 0 } }}
        content={
          <DashboardTable
            columns={columns}
            data={vmPortUsage.data}
            onRowClick={row => {
              allEntitiesDisclosure.onOpen();
              setDetails({
                panel: PanelsEnum.WellKnownPortsUnrestrictedAccess,
                iconType: IconTypes.Port,
                port: row?.port,
                total: row?.count,
              });
            }}
          />
        }
      />
      <AllEntitiesDrawer
        isOpen={allEntitiesDisclosure.isOpen}
        onClose={allEntitiesDisclosure.onClose}
        {...details}
      />
    </>
  );
};
